import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

/**
 * Where Clerk keeps the session token between launches.
 *
 * Without a cache, closing the app signs you out -- Clerk holds the token in
 * memory only. `expo-secure-store` puts it in the iOS keychain or Android
 * keystore rather than in plain AsyncStorage, which matters because this token
 * IS the credential: anything holding it can act as the user until it expires.
 *
 * On web there is no secure store, and `SecureStore` throws rather than
 * degrading. Returning undefined lets Clerk fall back to its own web storage
 * instead of the app failing to start -- which is what happens if you hand it
 * a cache whose methods reject.
 */
export interface TokenCache {
  getToken: (key: string) => Promise<string | null>;
  saveToken: (key: string, token: string) => Promise<void>;
  clearToken?: (key: string) => void;
}

export function createTokenCache(): TokenCache | undefined {
  if (Platform.OS === "web") return undefined;

  return {
    async getToken(key) {
      try {
        return await SecureStore.getItemAsync(key);
      } catch {
        // A corrupt or unreadable entry must not brick the app. Losing the
        // token means signing in again, which is recoverable; throwing here
        // means a launch crash, which is not.
        await SecureStore.deleteItemAsync(key).catch(() => {});
        return null;
      }
    },
    async saveToken(key, token) {
      try {
        await SecureStore.setItemAsync(key, token);
      } catch {
        // Failing to persist costs a re-login next launch. Failing loudly
        // costs the session the user is in the middle of.
      }
    },
  };
}
